import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, AlertCircle, CalendarCheck } from "lucide-react";
import { toast } from "sonner";
import { Calendar } from "@/components/ui/calendar";
import { getPaymentStatus, setBooking } from "@/lib/api";

const MAX_ATTEMPTS = 8;
const POLL_MS = 2000;

const fmtDate = (d) =>
    d.toLocaleDateString("en-NZ", {
        weekday: "long",
        day: "numeric",
        month: "long",
        year: "numeric",
    });

export default function PaymentSuccess() {
    const [params] = useSearchParams();
    const sessionId = params.get("session_id");
    const quoteId = params.get("quote_id");
    const [state, setState] = useState("checking"); // checking | paid | pending | error
    const [selected, setSelected] = useState(null);
    const [booking, setBookingState] = useState("idle"); // idle | saving | done
    const [bookedDate, setBookedDate] = useState(null);

    useEffect(() => {
        if (!sessionId) {
            setState("error");
            return;
        }
        let attempts = 0;
        let timer;
        const poll = async () => {
            try {
                const data = await getPaymentStatus(sessionId);
                if (data.payment_status === "paid") {
                    setState("paid");
                    return;
                }
                if (["expired", "failed"].includes(data.payment_status)) {
                    setState("error");
                    return;
                }
                attempts += 1;
                if (attempts >= MAX_ATTEMPTS) {
                    setState("pending");
                    return;
                }
                timer = setTimeout(poll, POLL_MS);
            } catch (e) {
                setState("error");
            }
        };
        poll();
        return () => clearTimeout(timer);
    }, [sessionId]);

    const confirmBooking = async () => {
        if (!selected || !quoteId) return;
        setBookingState("saving");
        try {
            // Local YYYY-MM-DD (avoid timezone shift from toISOString)
            const iso = `${selected.getFullYear()}-${String(selected.getMonth() + 1).padStart(2, "0")}-${String(selected.getDate()).padStart(2, "0")}`;
            await setBooking(quoteId, { dropoff_date: iso });
            setBookedDate(new Date(selected));
            setBookingState("done");
            toast.success("Drop-off day booked!");
        } catch (e) {
            toast.error(
                e?.response?.data?.detail ||
                    "Couldn't save your date. Please try again.",
            );
            setBookingState("idle");
        }
    };

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return (
        <div
            data-testid="payment-success-page"
            className="min-h-screen bg-carbon grid place-items-center px-6 py-12"
        >
            <div className="max-w-lg w-full glass rounded-3xl p-8 md:p-10 text-center shadow-glow-cyan">
                {state === "checking" && (
                    <div data-testid="payment-checking">
                        <Loader2
                            size={44}
                            className="text-safety animate-spin mx-auto"
                        />
                        <h1 className="font-display text-2xl text-white mt-6">
                            Confirming your deposit…
                        </h1>
                        <p className="text-slate-400 text-sm mt-2">
                            Just a moment while we check with our payment
                            provider.
                        </p>
                    </div>
                )}

                {state === "paid" && booking !== "done" && (
                    <div data-testid="payment-paid">
                        <CheckCircle2
                            size={48}
                            className="text-emerald-500 mx-auto"
                        />
                        <h1 className="font-display text-3xl text-white mt-6">
                            Your slot is locked in
                        </h1>
                        <p className="text-slate-400 mt-3 leading-relaxed">
                            Your{" "}
                            <span className="text-safety font-medium">
                                refundable NZ$50 hold
                            </span>{" "}
                            is confirmed and comes straight off your final bill.
                        </p>

                        {quoteId && (
                            <div
                                data-testid="booking-picker"
                                className="mt-8 border-t border-white/10 pt-8"
                            >
                                <div className="flex items-center justify-center gap-2 text-safety mb-1">
                                    <CalendarCheck size={18} />
                                    <span className="uppercase text-xs tracking-[0.25em]">
                                        Pick your drop-off day
                                    </span>
                                </div>
                                <p className="text-slate-400 text-sm mb-4">
                                    Choose a day that suits — we&rsquo;ll
                                    confirm the time with you.
                                </p>
                                <div className="flex justify-center">
                                    <Calendar
                                        mode="single"
                                        selected={selected}
                                        onSelect={setSelected}
                                        disabled={{ before: today }}
                                        data-testid="dropoff-calendar"
                                        className="rounded-xl border border-white/10"
                                    />
                                </div>
                                <button
                                    onClick={confirmBooking}
                                    disabled={!selected || booking === "saving"}
                                    data-testid="confirm-booking-button"
                                    className="mt-6 w-full py-3 text-sm uppercase tracking-[0.15em] text-white font-bold bg-safety hover:bg-safety-dark disabled:opacity-40 disabled:cursor-not-allowed transition-all inline-flex items-center justify-center gap-2 rounded-xl"
                                >
                                    {booking === "saving" ? (
                                        <>
                                            <Loader2
                                                size={16}
                                                className="animate-spin"
                                            />
                                            Saving…
                                        </>
                                    ) : selected ? (
                                        `Book ${fmtDate(selected)}`
                                    ) : (
                                        "Select a day above"
                                    )}
                                </button>
                            </div>
                        )}
                    </div>
                )}

                {state === "paid" && booking === "done" && (
                    <div data-testid="booking-confirmed">
                        <CalendarCheck
                            size={48}
                            className="text-emerald-500 mx-auto"
                        />
                        <h1 className="font-display text-3xl text-white mt-6">
                            You&rsquo;re booked in
                        </h1>
                        <p className="text-slate-400 mt-3 leading-relaxed">
                            Drop your car in on{" "}
                            <span
                                data-testid="booked-date"
                                className="text-safety font-medium"
                            >
                                {bookedDate && fmtDate(bookedDate)}
                            </span>
                            . We&rsquo;ll be in touch to confirm the time.
                        </p>
                    </div>
                )}

                {state === "pending" && (
                    <div data-testid="payment-pending">
                        <Loader2 size={44} className="text-safety mx-auto" />
                        <h1 className="font-display text-2xl text-white mt-6">
                            Payment is processing
                        </h1>
                        <p className="text-slate-400 mt-3">
                            This is taking a little longer than usual. If your
                            card was charged, you&rsquo;re all set — we&rsquo;ll
                            email you a confirmation shortly.
                        </p>
                    </div>
                )}

                {state === "error" && (
                    <div data-testid="payment-error">
                        <AlertCircle size={44} className="text-red-500 mx-auto" />
                        <h1 className="font-display text-2xl text-white mt-6">
                            We couldn&rsquo;t confirm your payment
                        </h1>
                        <p className="text-slate-400 mt-3">
                            No charge may have gone through. Please try again or
                            get in touch and we&rsquo;ll help.
                        </p>
                    </div>
                )}

                <Link
                    to="/"
                    data-testid="back-home-link"
                    className="inline-block mt-8 px-6 py-3 text-sm uppercase tracking-[0.15em] text-white font-bold bg-safety hover:bg-safety-dark shadow-glow-orange transition-all rounded-xl"
                >
                    Back to Panel Pros
                </Link>
            </div>
        </div>
    );
}
